/**
 * PushPromptProvider — app-wide "turn on notifications" orchestration.
 *
 * Agents were only ever offered push by a banner buried on the Notifications
 * screen, which is a screen you visit *because* you already came looking. So the
 * feature that exists to tell you about a signature or a payout the moment it
 * happens was off for anyone who never went hunting for it. This asks once, just
 * after sign-in, and leaves that banner in place for everyone who says no.
 *
 * The ask is deliberately in two steps: our own sheet first, the browser's
 * permission dialog only after the agent taps Turn on.
 *
 * That is not ceremony. `Notification.requestPermission()` can be answered once
 * and only once — a "Don't allow" is permanent for the origin and cannot be
 * re-requested from script, only undone by the agent digging through browser
 * settings they will never find. Firing it cold, with no explanation of what the
 * app would send, is how an app permanently loses the channel. A soft ask can be
 * declined harmlessly and asked again another day.
 *
 * Gating, in order of what would otherwise waste the one question we get:
 *   · signed in — a subscription is registered against the caller's agent row,
 *     so there is nothing to attach one to before login;
 *   · the browser actually supports push;
 *   · permission is still 'default' — 'denied' can never be re-asked from here,
 *     and 'granted' needs no dialog at all (see the silent re-subscribe below);
 *   · this DEVICE has no subscription yet. Device truth, not the server's
 *     `pushOn`, which is true if ANY of the agent's devices is subscribed and
 *     would leave a second phone silently unasked forever;
 *   · on iOS, the app is installed to the home screen. Web Push on iOS only
 *     exists in standalone mode, so asking inside a Safari tab produces a
 *     permission dialog that cannot lead anywhere;
 *   · the install sheet is not already on screen — two stacked sheets on first
 *     launch is how both get dismissed unread;
 *   · not dismissed in the last 30 days, matching the install nudge.
 */
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { useInstallPrompt } from './InstallPromptContext';
import { subscribeToPush } from '../push';

const PushPromptContext = createContext(null);

const DISMISSED_KEY = 'cid-agent-push-dismissed';
const RESHOW_DAYS = 30;

const supportsPush = () =>
  typeof window !== 'undefined'
  && 'serviceWorker' in navigator
  && 'PushManager' in window
  && 'Notification' in window;

const permissionNow = () => (supportsPush() ? Notification.permission : 'unsupported');

function isDismissedRecently() {
  try {
    const at = localStorage.getItem(DISMISSED_KEY);
    if (!at) return false;
    return (Date.now() - new Date(at).getTime()) / 86400000 < RESHOW_DAYS;
  } catch {
    return false;
  }
}

/** Does THIS browser already hold a push subscription? */
async function hasDeviceSubscription() {
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    return !!(await reg?.pushManager?.getSubscription());
  } catch {
    return false;
  }
}

export function PushPromptProvider({ children }) {
  const { isAuthed } = useAuth() || {};
  const { showPrompt: installSheetOpen, installed, platform } = useInstallPrompt() || {};

  const [supported] = useState(supportsPush);
  const [permission, setPermission] = useState(permissionNow);
  // null = not probed yet. The sheet stays down until we know, so it can never
  // flash at an agent who is already subscribed.
  const [subscribed, setSubscribed] = useState(null);
  const [dismissed, setDismissed] = useState(isDismissedRecently);
  const [manualOpen, setManualOpen] = useState(false);

  const refresh = useCallback(async () => {
    if (!supported) { setSubscribed(false); return false; }
    setPermission(permissionNow());
    const has = await hasDeviceSubscription();
    setSubscribed(has);
    return has;
  }, [supported]);

  // Probe on sign-in, and again whenever the tab is brought back — permission can
  // be changed in browser settings while the app sits in the background, and a
  // banner still offering to enable something already on reads as broken.
  useEffect(() => {
    if (!isAuthed) { setSubscribed(null); return undefined; }
    let live = true;
    const run = () => { if (live) refresh(); };
    run();
    const onVis = () => { if (document.visibilityState === 'visible') run(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { live = false; document.removeEventListener('visibilitychange', onVis); };
  }, [isAuthed, refresh]);

  /**
   * Permission already granted but no subscription on this device — a reinstall,
   * a cleared site data, or a service worker that was replaced. No dialog can
   * appear in this state, so re-attaching silently is strictly better than
   * showing a sheet to ask for something the agent already said yes to.
   */
  useEffect(() => {
    if (!isAuthed || !supported || subscribed !== false || permission !== 'granted') return;
    let live = true;
    subscribeToPush()
      .then(() => { if (live) setSubscribed(true); })
      .catch(() => { /* stays false; the sheet and the banner still offer it */ });
    return () => { live = false; };
  }, [isAuthed, supported, subscribed, permission]);

  const enable = useCallback(async () => {
    await subscribeToPush();
    setPermission(permissionNow());
    setSubscribed(true);
    setManualOpen(false);
    return true;
  }, []);

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISSED_KEY, new Date().toISOString());
    } catch {
      // private mode — the nudge simply returns next launch
    }
    setDismissed(true);
    setManualOpen(false);
  }, []);

  const triggerPrompt = useCallback(() => setManualOpen(true), []);
  const closePrompt = useCallback(() => setManualOpen(false), []);

  // iOS only delivers Web Push to a home-screen app. Everywhere else a browser
  // tab is a perfectly good push target.
  const platformReady = platform === 'ios' ? !!installed : true;

  const autoShow =
    !!isAuthed
    && supported
    && permission === 'default'
    && subscribed === false
    && platformReady
    && !installSheetOpen
    && !dismissed;

  const value = {
    supported,
    permission,
    // null while unknown — callers must not render an "off" state off a probe
    // that has not answered yet.
    subscribed,
    // True when the agent said no at the OS level: the in-app button cannot
    // recover this, so callers explain the settings route instead of offering
    // one that always throws.
    blocked: supported && permission === 'denied',
    showPrompt: autoShow || manualOpen,
    autoShow,
    manualOpen,
    enable,
    dismiss,
    triggerPrompt,
    closePrompt,
    refresh,
  };

  return <PushPromptContext.Provider value={value}>{children}</PushPromptContext.Provider>;
}

export function usePushPrompt() {
  const ctx = useContext(PushPromptContext);
  if (!ctx) throw new Error('usePushPrompt must be used within a PushPromptProvider');
  return ctx;
}

export default PushPromptContext;
